import { PeakLevel } from "./PeakData.js";
import { WaveformSource } from "./WaveformSource.js";
import { remoteFetch } from "../core/remoteFetch.js";

/**
 * Waveform peaks fetched from the af backend, per ANALYSIS_API_SPEC.md §1.
 *
 * The overview level is fetched whole on load — a few KB, and enough to draw the
 * entire asset. Finer levels are fetched a block at a time for whatever the
 * viewport is looking at, so an 8-hour file costs the same to navigate as a
 * 30-second one.
 */

/**
 * Concurrent block requests. Enough to fill a viewport in one round trip
 * without burying the connection when someone drags the zoom slider.
 */
const MAX_CONCURRENT_PEAK_REQUESTS = 6;

export class RemoteWaveform extends WaveformSource {
  #baseUrl;
  #blockPeaks;
  #inFlight = new Set();
  #queue = [];
  #failed = new Set();
  #destroyed = false;

  /**
   * @param {Object} manifest - parsed manifest.json
   * @param {string} manifestUrl - used to resolve endpoint URLs, per the spec
   * @param {Object} [options]
   * @param {Function} [options.onChange] - called when new peaks land
   * @param {RequestInit|Function} [options.fetchOptions] - auth etc. for every request
   */
  constructor(manifest, manifestUrl, options = {}) {
    super();

    this.manifest = manifest;

    // Endpoints resolve relative to the manifest's own URL, per the spec. That
    // needs an absolute base, so a caller-supplied relative path like
    // "/api/audio/x/manifest.json" is resolved against the document first.
    const documentBase = typeof document !== "undefined" ? document.baseURI : undefined;
    this.#baseUrl = new URL(manifestUrl, documentBase).toString();
    this.#blockPeaks = manifest.peaks.blockPeaks;
    this.onChange = options.onChange || null;
    this.fetchOptions = options.fetchOptions || null;

    this.stats = { requests: 0, bytes: 0, failures: 0 };

    // The overview arrives as one block; finer levels are blocked so they can be
    // partly resident.
    for (const samplesPerPeak of manifest.peaks.levels) {
      const count = Math.ceil(manifest.sampleCount / samplesPerPeak);
      const isOverview = samplesPerPeak === manifest.peaks.overview;
      this.addLevel(new PeakLevel(samplesPerPeak, count, isOverview ? count : this.#blockPeaks));
    }
  }

  #url(spp, startPeak, count) {
    const url = new URL("peaks", this.#baseUrl);
    url.searchParams.set("spp", String(spp));
    url.searchParams.set("start", String(startPeak));
    url.searchParams.set("count", String(count));
    return url.toString();
  }

  /**
   * Fetch the overview level in full. Resolving this is what makes the track
   * drawable — everything after it is refinement.
   */
  async loadOverview() {
    const samplesPerPeak = this.manifest.peaks.overview;
    const level = this.levels.get(samplesPerPeak);

    const data = await this.#fetchPeaks(samplesPerPeak, 0, level.count);
    level.setBlock(0, data);
    this.onChange?.();
  }

  async #fetchPeaks(spp, startPeak, count) {
    const response = await remoteFetch(this.#url(spp, startPeak, count), this.fetchOptions);
    if (!response.ok) {
      throw new Error(`peaks ${spp}@${startPeak} failed: ${response.status}`);
    }

    const buffer = await response.arrayBuffer();
    this.stats.requests++;
    this.stats.bytes += buffer.byteLength;

    // A short read at the end of the asset is normal, not an error.
    return new Int16Array(buffer);
  }

  /**
   * Queue whatever the viewport needs that isn't resident.
   *
   * The queue is *replaced* rather than appended to: by the time this is called
   * again the view has moved, and blocks for where the user used to be looking
   * are no longer worth fetching. In-flight requests are deliberately left to
   * finish — they're 16 KB, the data stays valid, and cancelling them would
   * throw away work that a scroll back would immediately need again.
   */
  request(samplesPerPeak, startSample, endSample) {
    if (this.#destroyed) return;

    const level = this.levels.get(samplesPerPeak);
    if (!level) return;

    const startPeak = Math.floor(startSample / samplesPerPeak);
    const endPeak = Math.ceil(endSample / samplesPerPeak);

    this.#queue = level
      .missingBlocks(startPeak, endPeak)
      .map((block) => ({ samplesPerPeak, block }))
      .filter(({ block }) => {
        const key = `${samplesPerPeak}:${block}`;
        return !this.#inFlight.has(key) && !this.#failed.has(key);
      });

    this.#pump();
  }

  #pump() {
    while (this.#inFlight.size < MAX_CONCURRENT_PEAK_REQUESTS && this.#queue.length > 0) {
      const { samplesPerPeak, block } = this.#queue.shift();
      const key = `${samplesPerPeak}:${block}`;
      if (this.#inFlight.has(key)) continue;

      this.#inFlight.add(key);
      const level = this.levels.get(samplesPerPeak);

      this.#fetchPeaks(samplesPerPeak, block * this.#blockPeaks, this.#blockPeaks)
        .then((data) => {
          if (this.#destroyed) return;
          level.setBlock(block, data);
          this.onChange?.();
        })
        .catch(() => {
          // Don't retry forever on a block the server won't serve; one bad
          // block shouldn't turn into a request storm every frame.
          this.stats.failures++;
          this.#failed.add(key);
        })
        .finally(() => {
          this.#inFlight.delete(key);
          if (!this.#destroyed) this.#pump();
        });
    }
  }

  /** Whether anything is still being fetched — drives a loading indicator. */
  get isFetching() {
    return this.#inFlight.size > 0;
  }

  destroy() {
    this.#destroyed = true;
    this.#queue = [];
    super.destroy();
  }
}
